import React, { useCallback, useMemo } from "react";
import {
  Button,
  Dialog,
  Divider,
  Link as MuiLink,
  Stack,
  Typography,
  useTheme
} from "@mui/material";
import { Business, EventBusy, Home } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { DialogHeaderWithClose } from "../../../components/Dialog/DialogHeaderWithClose.component";
import { SeeditDialogContent } from "../../../components/Dialog/SeeditDialogContent.component";
import type { BookingScheduleInterface } from "../../Booking-Form/typings/booking.types";
import type {
  Dashboard2DayBookingSummary,
  DayWorkStatus,
  DayWorkStatusKind
} from "../types/dashboard2-work-status.types";
import { dashboard2ManageColleaguesLinkSx } from "../dashboard2-status.styles";
import { extraBookingCount } from "../utils/dashboard2-status-display.utils";
import { Dashboard2AdditionalBookingsList } from "./Dashboard2AdditionalBookingsList";

type Dashboard2StatusSheetProps = {
  open: boolean;
  dayLabel: string;
  currentStatus: DayWorkStatus;
  onClose: () => void;
  onSelectStatus: (kind: DayWorkStatusKind) => void;
  onBookingClick?: (booking: Dashboard2DayBookingSummary) => void;
  primarySchedule?: BookingScheduleInterface;
  onPrimaryScheduleClick?: (schedule: BookingScheduleInterface) => void;
  /** When false, mobile working is hidden (company setting). Defaults true for isolated tests. */
  mobileWorkingEnabled?: boolean;
};

export const Dashboard2StatusSheet: React.FC<Dashboard2StatusSheetProps> = ({
  open,
  dayLabel,
  currentStatus,
  onClose,
  onSelectStatus,
  onBookingClick,
  primarySchedule,
  onPrimaryScheduleClick,
  mobileWorkingEnabled = true
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isHoliday = currentStatus.kind === "public_holiday";
  const additionalBookings = currentStatus.additionalBookings ?? [];
  const hasAdditionalBookings = extraBookingCount(currentStatus) > 0;

  const handleBookingClick = useCallback(
    (booking: Dashboard2DayBookingSummary) => {
      if (!onBookingClick) {
        return;
      }

      onBookingClick(booking);
      onClose();
    },
    [onBookingClick, onClose]
  );

  const showCurrentBookingLink =
    (currentStatus.kind === "office" || currentStatus.kind === "mobile_working") &&
    !!primarySchedule &&
    !!onPrimaryScheduleClick;

  const handlePrimaryScheduleClick = useCallback(() => {
    if (!primarySchedule || !onPrimaryScheduleClick) {
      return;
    }

    onPrimaryScheduleClick(primarySchedule);
    onClose();
  }, [onClose, onPrimaryScheduleClick, primarySchedule]);

  const options: Array<{ kind: DayWorkStatusKind; label: string; icon: React.ReactNode }> =
    useMemo(() => {
      const allOptions: Array<{ kind: DayWorkStatusKind; label: string; icon: React.ReactNode }> = [
        { kind: "office", label: t("_dashboard2_statusActionOffice"), icon: <Business /> },
        {
          kind: "mobile_working",
          label: t("_dashboard2_statusActionMobile"),
          icon: <Home />
        },
        { kind: "unplanned", label: t("_dashboard2_statusActionNotSet"), icon: <EventBusy /> }
      ];

      if (mobileWorkingEnabled) {
        return allOptions;
      }

      return allOptions.filter(option => option.kind !== "mobile_working");
    }, [mobileWorkingEnabled, t]);

  return (
    <Dialog open={open} fullWidth maxWidth="xs" data-testid="dashboard2-status-sheet">
      <DialogHeaderWithClose
        title={t("_dashboard2_statusSheetTitle", { day: dayLabel })}
        onClose={onClose}
        closeTestId="dashboard2-status-sheet-close"
      />
      <SeeditDialogContent>
        {isHoliday && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t("_dashboard2_statusSheetHolidayHint", {
              holiday: currentStatus.holidayName ?? currentStatus.label
            })}
          </Typography>
        )}
        <Stack spacing={1.5}>
          <Stack spacing={1}>
            {options.map(option => {
              const isSelected = currentStatus.kind === option.kind;

              return (
                <Button
                  key={option.kind}
                  variant={isSelected ? "contained" : "outlined"}
                  color={option.kind === "mobile_working" ? "info" : "primary"}
                  startIcon={option.icon}
                  onClick={() => onSelectStatus(option.kind)}
                  aria-pressed={isSelected}
                  data-testid={`dashboard2-status-sheet-option-${option.kind}`}
                  fullWidth
                  sx={{ justifyContent: "flex-start" }}
                >
                  {option.label}
                </Button>
              );
            })}
          </Stack>

          {showCurrentBookingLink && (
            <MuiLink
              component="button"
              type="button"
              variant="inherit"
              onClick={handlePrimaryScheduleClick}
              sx={dashboard2ManageColleaguesLinkSx(theme)}
              data-testid="dashboard2-status-sheet-show-current-booking"
            >
              {t("_dashboard2_statusSheetShowCurrentBooking")}
            </MuiLink>
          )}

          {hasAdditionalBookings && (
            <>
              <Divider />
              <Typography variant="subtitle2">
                {t("_dashboard2_additionalBookingsTitle")}
              </Typography>
              <Dashboard2AdditionalBookingsList
                bookings={additionalBookings}
                onBookingClick={handleBookingClick}
              />
            </>
          )}
        </Stack>
      </SeeditDialogContent>
    </Dialog>
  );
};
